<template>
  <section class="panel">
    <div v-if="hasUnread" class="notification-actions">
      <button type="button" class="btn btn-secondary" @click="$emit('mark-all-read')">
        Отметить все прочитанными
      </button>
    </div>

    <p v-if="loading" class="empty-text">Загрузка...</p>
    <p v-else-if="notifications.length === 0" class="empty-text">Пока нет уведомлений.</p>

    <ul v-else class="notification-list">
      <li
        v-for="notification in notifications"
        :key="notification.id"
        class="notification-item track-item"
        :class="{ unread: !notification.read_at }"
        @click="$emit('select', notification)"
      >
        <UserIdentity :user="notification.actor" size="sm" />
        <p class="notification-text">{{ notificationText(notification) }}</p>
        <time class="notification-time">{{ formatTime(notification.created_at) }}</time>
      </li>
    </ul>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import UserIdentity from './UserIdentity.vue'
import { userDisplayName } from '../utils/userDisplay'

const props = defineProps({
  notifications: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

defineEmits(['select', 'mark-all-read'])

const hasUnread = computed(() => props.notifications.some((item) => !item.read_at))

function reactionLabel(kind) {
  return kind === 'dislike' ? 'дизлайк' : 'лайк'
}

function notificationText(notification) {
  const actor = userDisplayName(notification.actor)
  const track = notification.track_name || 'трек'
  const trip = notification.trip_name || 'поездку'
  const eventName = notification.event_name || 'событие'

  switch (notification.kind) {
    case 'comment_reaction':
      return `${actor} поставил(а) ${reactionLabel(notification.metadata?.reaction_kind)} вашему комментарию к треку «${track}»`
    case 'track_reaction':
      return `${actor} поставил(а) ${reactionLabel(notification.metadata?.reaction_kind)} вашему треку «${track}»`
    case 'trip_reaction':
      return `${actor} поставил(а) ${reactionLabel(notification.metadata?.reaction_kind)} вашей поездке «${trip}»`
    case 'event_reaction':
      return `${actor} поставил(а) ${reactionLabel(notification.metadata?.reaction_kind)} вашему событию «${eventName}»`
    case 'trip_comment_reaction':
      return `${actor} поставил(а) ${reactionLabel(notification.metadata?.reaction_kind)} вашему комментарию к поездке «${trip}»`
    case 'event_comment_reaction':
      return `${actor} поставил(а) ${reactionLabel(notification.metadata?.reaction_kind)} вашему комментарию к событию «${eventName}»`
    case 'favorite_author_new_track':
      return `${actor} опубликовал(а) новый трек «${track}»`
    case 'favorite_author_new_trip':
      return `${actor} опубликовал(а) новую поездку «${trip}»`
    case 'favorite_author_new_event':
      return `${actor} опубликовал(а) новое событие «${eventName}»`
    case 'comment_mention':
      if (notification.event_id) {
        return `${actor} упомянул(а) вас в комментарии к событию «${eventName}»`
      }
      if (notification.trip_id) {
        return `${actor} упомянул(а) вас в комментарии к поездке «${trip}»`
      }
      return `${actor} упомянул(а) вас в комментарии к треку «${track}»`
    case 'track_comment':
      return `${actor} оставил(а) комментарий к вашему треку «${track}»`
    case 'trip_comment':
      return `${actor} оставил(а) комментарий к вашей поездке «${trip}»`
    case 'event_comment':
      return `${actor} оставил(а) комментарий к вашему событию «${eventName}»`
    case 'trip_companion_added':
      return `${actor} добавил(а) вас в поездку «${trip}»`
    case 'event_participant_joined':
      return `${actor} присоединился(ась) к событию «${eventName}»`
    case 'event_updated':
      return `${actor} обновил(а) событие «${eventName}»`
    default:
      if (notification.event_id) return `${actor} — ${eventName}`
      if (notification.trip_id) return `${actor} — ${trip}`
      return `${actor} — ${track}`
  }
}

function formatTime(value) {
  if (!value) return ''

  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}
</script>

<style scoped>
.notification-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.75rem;
}

.notification-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.notification-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.375rem;
}

.notification-item.unread {
  background: #eff6ff;
}

.notification-text {
  margin: 0;
}

.notification-time {
  color: #64748b;
  font-size: 0.875rem;
}

.empty-text {
  margin: 0;
  color: #64748b;
}
</style>
